import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ScannerCodice from "@/components/ScannerCodice";
import {
  AlertTriangle, Check, Download, Lock, RotateCcw, ScanLine, Trash2, Unlock,
  Upload,
} from "lucide-react";

/**
 * Smistamento dei contatori gas resi a Italgas, pallet per pallet.
 *
 * Chi sta in magazzino prende un contatore dal mucchio, inquadra il codice, e
 * lo schermo gli dice lo stato, l'etichetta di garanzia, e su quale pallet va.
 * I pallet li apre l'app: la prima volta che passa una coppia stato+etichetta
 * nuova ne nasce uno col suo numero, e da lì tutti quelli uguali vanno lì.
 * Pieno il pallet, si tocca «chiudi» e il prossimo uguale ne apre un altro.
 *
 * Tutto è pensato per essere letto da lontano e toccato con i guanti: un
 * colore che riempie lo schermo, il numero del pallet grande, un pulsante.
 */
const COLORI = {
  verde: "bg-emerald-500 text-white",
  blu: "bg-[#0067b2] text-white",
  rosso: "bg-red-600 text-white",
  giallo: "bg-amber-400 text-amber-950",
  grigio: "bg-slate-700 text-white",
};
const PASTIGLIE = {
  verde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blu: "bg-blue-50 text-blue-700 border-blue-200",
  rosso: "bg-red-50 text-red-700 border-red-200",
  giallo: "bg-amber-50 text-amber-800 border-amber-200",
  grigio: "bg-slate-100 text-slate-600 border-slate-200",
};
const giorno = (iso) => (iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "");

const scaricaFile = (data, nome) => {
  const u = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = u; a.download = nome; a.click();
  window.URL.revokeObjectURL(u);
};

export default function Contatori() {
  const { user } = useAuth();
  const puoCaricare = ["admin", "amministrativo"].includes(user?.role);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [scanner, setScanner] = useState(false);
  const [esito, setEsito] = useState(null);
  const [importa, setImporta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [daChiudere, setDaChiudere] = useState(null);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/contatori?limite=1");
      setDati(data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const aggiorna = (data) => setDati((d) => ({
    ...d,
    riepilogo: data.riepilogo || d.riepilogo,
    pallet: data.pallet || d.pallet,
  }));

  const cerca = async (codice) => {
    setScanner(false);
    try {
      const { data } = await api.get(`/contatori/cerca?codice=${encodeURIComponent(codice)}`);
      setEsito(data);
      // Se la scansione ha aperto un pallet nuovo, l'elenco sotto lo deve
      // già mostrare: è lì che si legge che numero attaccargli.
      if (data.pallet?.appena_nato) {
        const { data: p } = await api.get("/contatori/pallet");
        aggiorna(p);
      }
    } catch {
      toast.error("Errore nella ricerca");
    }
  };

  const conferma = async () => {
    if (!esito?.contatore) return;
    setSalvando(true);
    try {
      const { data } = await api.post(`/contatori/${esito.contatore.id}/smistato`, {});
      const { data: p } = await api.get("/contatori/pallet");
      aggiorna({ riepilogo: data.riepilogo, pallet: p.pallet });
      toast.success(`${esito.contatore.matricola} sul pallet ${data.pallet_info?.numero}`);
      setEsito(null);
      setScanner(true);            // pronto per il prossimo, senza tocchi in più
    } catch {
      toast.error("Errore");
    } finally { setSalvando(false); }
  };

  const annulla = async () => {
    if (!esito?.contatore) return;
    try {
      const { data } = await api.post(`/contatori/${esito.contatore.id}/annulla`);
      const { data: p } = await api.get("/contatori/pallet");
      aggiorna({ riepilogo: data.riepilogo, pallet: p.pallet });
      toast.success("Tolto dal pallet");
      setEsito(null);
    } catch { toast.error("Errore"); }
  };

  const chiudi = async () => {
    const p = daChiudere;
    setDaChiudere(null);
    try {
      const { data } = await api.post(`/contatori/pallet/${p.numero}/chiudi`);
      aggiorna(data);
      toast.success(`Pallet ${p.numero} chiuso: ${p.contatori} contatori`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const riapri = async (p) => {
    try {
      const { data } = await api.post(`/contatori/pallet/${p.numero}/riapri`);
      aggiorna(data);
      toast.success(`Pallet ${p.numero} riaperto`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const esporta = async (numero) => {
    try {
      const url = numero !== undefined ? `/contatori/esporta?pallet=${numero}` : "/contatori/esporta";
      const { data, headers } = await api.get(url, { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1]
        || (numero !== undefined ? `Contatori_Italgas_pallet_${numero}.xlsx` : "Contatori_Italgas.xlsx");
      scaricaFile(data, nome);
    } catch { toast.error("Errore nell'esportazione"); }
  };

  if (carico) return <p className="text-sm text-slate-500">Caricamento...</p>;
  if (!dati) return null;

  const r = dati.riepilogo;
  const restano = r.totale - r.smistati;
  const pallet = dati.pallet || [];
  const aperti = pallet.filter((p) => !p.chiuso_il);
  const chiusi = pallet.filter((p) => p.chiuso_il);

  return (
    <div className="space-y-5" data-testid="contatori-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Magazzino</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Contatori Italgas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Inquadra il contatore e leggi su quale pallet va
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {r.totale > 0 && (
            <Button variant="outline" className="h-12" onClick={() => esporta()}
                    data-testid="esporta-tutto">
              <Download className="w-4 h-4 mr-2" /> Excel per la spedizione
            </Button>
          )}
          {puoCaricare && (
            <Button variant="outline" className="h-12" onClick={() => setImporta(true)}
                    data-testid="importa-contatori">
              <Upload className="w-4 h-4 mr-2" /> Carica l&apos;elenco
            </Button>
          )}
        </div>
      </div>

      {r.totale === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center text-slate-500">
            Nessun elenco caricato. Carica il foglio mandato da Italgas per
            cominciare.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Da fare", restano, ""], ["Sul pallet", r.smistati, "text-emerald-600"],
              ["Pallet aperti", r.pallet_aperti, "text-[#0067b2]"],
              ["Pallet chiusi", r.pallet_chiusi, "text-slate-500"]].map(([n, v, c]) => (
                <div key={n} className="rounded-lg border border-slate-200 p-3">
                  <div className={`font-display text-3xl font-black tabular-nums ${c}`}>{v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{n}</div>
                </div>
              ))}
          </div>

          <Button onClick={() => { setEsito(null); setScanner(true); }}
                  data-testid="apri-scanner"
                  className="w-full h-20 text-lg bg-orange-500 hover:bg-orange-600 text-white">
            <ScanLine className="w-7 h-7 mr-3" /> Inquadra un contatore
          </Button>
        </>
      )}

      {esito && (
        <Card className="border-slate-200 overflow-hidden">
          <div className={`p-6 text-center ${COLORI[esito.stato.colore] || COLORI.grigio}`}>
            <div className="font-display text-3xl sm:text-4xl font-black tracking-tight"
                 data-testid="esito-stato">
              {esito.stato.nome}
            </div>
            {esito.trovato ? (
              <div className="text-sm mt-2 opacity-90">
                Etichetta garanzia: <span className="font-bold text-base" data-testid="esito-etichetta">
                  {esito.stato.etichetta_garanzia}
                </span>
              </div>
            ) : (
              <p className="text-sm mt-2 opacity-90">{esito.stato.cosa_fare}</p>
            )}
          </div>
          <CardContent className="p-4 space-y-3">
            {esito.trovato ? (
              <>
                {esito.pallet && (
                  <div className="text-center py-2" data-testid="esito-pallet">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {esito.contatore.pallet !== null && esito.contatore.pallet !== undefined
                        ? "Sta sul pallet" : esito.pallet.appena_nato ? "Apri un pallet nuovo" : "Va sul pallet"}
                    </div>
                    <div className="font-display text-6xl font-black tabular-nums tracking-tighter">
                      {esito.pallet.numero}
                    </div>
                    {esito.pallet.appena_nato && (
                      <div className="text-xs text-orange-700 font-semibold mt-1">
                        Primo di questo tipo: prendi un bancale vuoto e scrivici il {esito.pallet.numero}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-sm">
                  <div className="font-mono font-bold text-lg">
                    {esito.contatore.matricola}
                  </div>
                  <div className="text-slate-500 text-xs mt-0.5">
                    {esito.contatore.data_rimozione && <>rimosso il {giorno(esito.contatore.data_rimozione)}</>}
                    {esito.riconosciuto === "dalle cifre" && <> · riconosciuto dalle sole cifre</>}
                  </div>
                  {esito.contatore.note && (
                    <div className="text-xs text-slate-600 mt-1">{esito.contatore.note}</div>
                  )}
                </div>

                {esito.contatore.pallet !== null && esito.contatore.pallet !== undefined ? (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      Questo contatore risulta già sul pallet {esito.contatore.pallet}
                      {esito.contatore.smistato_il && <> dal {giorno(esito.contatore.smistato_il)}</>}.
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1 h-12" onClick={annulla}
                              data-testid="annulla-smistamento">
                        <RotateCcw className="w-4 h-4 mr-2" /> Toglilo dal pallet
                      </Button>
                      <Button className="flex-1 h-12 bg-slate-700 hover:bg-slate-800 text-white"
                              onClick={() => { setEsito(null); setScanner(true); }}>
                        Avanti
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" className="h-14 px-4"
                            onClick={() => { setEsito(null); setScanner(true); }}>
                      Salta
                    </Button>
                    <Button onClick={conferma} disabled={salvando}
                            data-testid="conferma-smistato"
                            className="flex-1 h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="w-5 h-5 mr-2" />
                      {salvando ? "..." : `Messo sul pallet ${esito.pallet?.numero ?? ""}`}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div className="text-sm">
                  Codice letto: <span className="font-mono font-bold">{esito.codice_letto}</span>
                  {esito.candidati && (
                    <div className="text-xs text-slate-600 mt-1">
                      Potrebbe essere: {esito.candidati.join(", ")}
                    </div>
                  )}
                </div>
                <Button className="w-full h-14 bg-slate-700 hover:bg-slate-800 text-white"
                        onClick={() => { setEsito(null); setScanner(true); }}>
                  Avanti
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* I pallet che si stanno caricando: uno per riquadro, col numero
          grande, cosa ci va sopra, e quanti ce ne sono. «Chiudi» quando è
          pieno. */}
      {aperti.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Pallet aperti — quelli che stai caricando
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {aperti.map((p) => (
              <Card key={p.numero} className="border-slate-200" data-testid={`pallet-${p.numero}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`font-display text-4xl font-black tabular-nums w-16 text-center rounded-lg py-2 ${COLORI[p.colore] || COLORI.grigio}`}>
                    {p.numero}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.stato || "senza stato"}</div>
                    <div className="text-xs text-slate-500">
                      Etichetta garanzia: <b>{p.etichetta_garanzia || "—"}</b>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <b className="tabular-nums">{p.contatori}</b> {p.contatori === 1 ? "contatore" : "contatori"}
                      {" · "}aperto il {giorno(p.aperto_il)}
                    </div>
                  </div>
                  <Button className="h-12 bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => setDaChiudere(p)} data-testid={`chiudi-pallet-${p.numero}`}>
                    <Lock className="w-4 h-4 mr-2" /> Chiudi
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {chiusi.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Pallet chiusi — pronti per la spedizione
          </div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {chiusi.map((p) => (
              <div key={p.numero} className="p-3 flex items-center gap-3 text-sm"
                   data-testid={`pallet-chiuso-${p.numero}`}>
                <span className={`font-display text-xl font-black tabular-nums w-10 text-center rounded py-1 ${PASTIGLIE[p.colore] || PASTIGLIE.grigio} border`}>
                  {p.numero}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{p.stato || "senza stato"}</span>
                  <span className="text-slate-500"> · garanzia {p.etichetta_garanzia || "—"}</span>
                  <div className="text-xs text-slate-500">
                    {p.contatori} contatori · chiuso il {giorno(p.chiuso_il)}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => esporta(p.numero)}
                        title="Il foglio di questo pallet" data-testid={`esporta-pallet-${p.numero}`}>
                  <Download className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => riapri(p)}
                        title="Chiuso per sbaglio: riaprilo">
                  <Unlock className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {scanner && (
        <ScannerCodice titolo="Inquadra il contatore"
                       onLetto={cerca} onClose={() => setScanner(false)} />
      )}

      {daChiudere && (
        <Dialog open onOpenChange={() => setDaChiudere(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Chiudere il pallet {daChiudere.numero}?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              Sopra ci sono <b>{daChiudere.contatori}</b> contatori
              «{daChiudere.stato}» con etichetta garanzia <b>{daChiudere.etichetta_garanzia || "—"}</b>.
              Da adesso il prossimo contatore uguale apre un pallet nuovo.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDaChiudere(null)}>Annulla</Button>
              <Button onClick={chiudi} data-testid="conferma-chiusura"
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                <Lock className="w-4 h-4 mr-2" /> Chiudi il pallet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {importa && (
        <ImportaElenco onChiudi={() => setImporta(false)}
                       onFatto={() => { setImporta(false); carica(); }} />
      )}
    </div>
  );
}

/** L'elenco mandato da Italgas: prima si guarda, poi si applica. */
function ImportaElenco({ onChiudi, onFatto }) {
  const [file, setFile] = useState(null);
  const [esito, setEsito] = useState(null);
  const [sostituisci, setSostituisci] = useState(false);
  const [busy, setBusy] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("solo_verifica", soloVerifica ? "true" : "false");
    fd.append("sostituisci", sostituisci ? "true" : "false");
    try {
      const { data } = await api.post("/contatori/importa", fd,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.importati} contatori caricati`);
        onFatto();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Carica l&apos;elenco dei contatori</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Le colonne si riconoscono dal titolo: <strong>matricola</strong>,
            <strong> data rimozione</strong>, <strong>etichetta garanzia</strong>,
            <strong> numero pallet</strong>, <strong>stato</strong>. Lo stato si
            tiene com&apos;è scritto. Se il numero del pallet è già compilato su
            qualche riga, quei contatori entrano già sistemati e i loro pallet
            nascono chiusi.
          </p>

          <div>
            <Label>File Excel</Label>
            <Input type="file" accept=".xlsx,.xlsm,.xls"
                   onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={sostituisci} data-testid="sostituisci"
                   onChange={(e) => { setSostituisci(e.target.checked); setEsito(null); }} />
            Sostituisci l&apos;elenco che c&apos;è già
          </label>
          {sostituisci && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex gap-2">
              <Trash2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Toglie tutto quello caricato prima, pallet compresi. Non si annulla.
            </p>
          )}

          {esito && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-2">
                {[["Nel file", esito.righe_nel_file],
                  ["Da caricare", esito.da_importare],
                  ["Già presenti", (esito.gia_presenti || []).length]].map(([n, v]) => (
                    <div key={n} className="border border-slate-200 rounded p-2">
                      <div className="font-display text-xl font-black tabular-nums">{v}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{n}</div>
                    </div>
                  ))}
              </div>
              {(esito.per_coppia || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    Stato · etichetta garanzia — ogni coppia farà i suoi pallet
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {esito.per_coppia.map((c) => (
                      <span key={c.coppia} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {c.coppia}: <strong>{c.quanti}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-500">
                Foglio «{esito.foglio}» · matricola da «{esito.colonne_lette?.matricola}»
                {esito.colonne_lette?.stato && <>, stato da «{esito.colonne_lette.stato}»</>}
                {esito.colonne_lette?.etichetta_garanzia && <>, garanzia da «{esito.colonne_lette.etichetta_garanzia}»</>}
                {esito.colonne_lette?.pallet && <>, pallet da «{esito.colonne_lette.pallet}»</>}.
              </p>
              {esito.gia_su_pallet > 0 && (
                <p className="text-xs text-slate-600">
                  {esito.gia_su_pallet} righe hanno già il numero del pallet nel file:
                  entrano come sistemate.
                </p>
              )}
              {esito.senza_stato > 0 && (
                <p className="text-xs text-amber-700">
                  {esito.senza_stato} righe senza stato: entrano lo stesso, e faranno
                  pallet «senza stato».
                </p>
              )}
              {(esito.scartate || []).length > 0 && (
                <p className="text-xs text-red-700">
                  {esito.scartate.length} righe scartate:{" "}
                  {esito.scartate.slice(0, 5).map((s) => `riga ${s.riga}`).join(", ")}
                  {esito.scartate.length > 5 && " …"}
                </p>
              )}
              {(esito.doppi_nel_file || []).length > 0 && (
                <p className="text-xs text-amber-700">
                  {esito.doppi_nel_file.length} matricole ripetute nel file: ne
                  entra una sola.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          <Button variant="outline" disabled={!file || busy} onClick={() => manda(true)}
                  data-testid="verifica-elenco">
            Guarda cosa succede
          </Button>
          <Button disabled={!file || busy || !esito} onClick={() => manda(false)}
                  data-testid="carica-elenco"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "..." : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
